import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, ShieldCheck, Factory } from "lucide-react";

const HomeLearnMore = () => {
  const navigate = useNavigate();

  return (
    <section className="relative overflow-hidden bg-[#f5f5f5] py-16 md:py-24">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(255,0,0,0.06),_transparent_28%),radial-gradient(circle_at_bottom_left,_rgba(0,0,0,0.05),_transparent_32%)]" />
        <div className="absolute top-10 left-[-100px] h-72 w-72 rounded-full bg-[#FF0000]/10 blur-3xl" />
        <div className="absolute bottom-0 right-[-80px] h-80 w-80 rounded-full bg-black/10 blur-3xl" />
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
        <div className="grid items-center gap-10 md:grid-cols-2 lg:gap-14">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
            viewport={{ once: true }}
            className="order-2 md:order-1"
          >
            {/* Badge */}
            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-black/10 bg-white/80 px-4 py-2 text-xs sm:text-sm font-medium text-black shadow-lg backdrop-blur-xl">
              <Sparkles size={14} className="text-[#FF0000]" />
              Innovation Backed by 13+ Years of Expertise
            </div>

            {/* Heading */}
            <h2 className="text-3xl font-bold tracking-tight text-black sm:text-4xl md:text-5xl">
              <span className="text-[#FF0000]">Learn</span> About Innovation at
              <span className="font-orbitron"> IES Gear</span>
            </h2>

            {/* Paragraphs */}
            <p className="mt-5 text-sm leading-relaxed text-gray-700 sm:text-base md:text-lg">
              <span className="font-semibold text-black">IES Gear</span> has
              spent over 13 years refining the art of boxing and sportswear
              manufacturing. As a globally recognized producer, we specialize in
              premium boxing gloves, training gear, and sportswear engineered
              for professionals and performance-driven athletes.
            </p>

            <p className="mt-4 text-sm leading-relaxed text-gray-700 sm:text-base md:text-lg">
              Our production facility is powered by advanced machinery,
              precision craftsmanship, and strict quality standards—ensuring
              every product delivers durability, comfort, and world-class
              performance from raw material to final finish.
            </p>

            {/* Highlights */}
            <div className="mt-6 grid gap-3 sm:grid-cols-3">
              <div className="rounded-2xl border border-black/10 bg-white/80 px-4 py-3 shadow-lg backdrop-blur-xl">
                <div className="mb-2 flex items-center">
                  <Factory size={18} className="text-[#FF0000]" />
                </div>
                <p className="text-sm font-semibold text-black">Advanced Facility</p>
                <p className="text-xs text-gray-600">Built for precision</p>
              </div>

              <div className="rounded-2xl border border-black/10 bg-white/80 px-4 py-3 shadow-lg backdrop-blur-xl">
                <div className="mb-2 flex items-center">
                  <ShieldCheck size={18} className="text-[#FF0000]" />
                </div>
                <p className="text-sm font-semibold text-black">Strict QC</p>
                <p className="text-xs text-gray-600">Premium consistency</p>
              </div>

              <div className="rounded-2xl border border-black/10 bg-white/80 px-4 py-3 shadow-lg backdrop-blur-xl">
                <div className="mb-2 flex items-center">
                  <Sparkles size={18} className="text-[#FF0000]" />
                </div>
                <p className="text-sm font-semibold text-black">Luxury Finish</p>
                <p className="text-xs text-gray-600">Refined craftsmanship</p>
              </div>
            </div>

            {/* CTA */}
            <div className="mt-8">
              <button
                onClick={() => navigate("/about")}
                aria-label="Navigate to About Us page"
                className="group inline-flex items-center gap-2 rounded-2xl bg-black px-7 py-4 text-sm font-semibold text-white shadow-2xl transition-all duration-300 hover:-translate-y-1 hover:bg-[#111] sm:text-base"
              >
                Learn More
                <ArrowRight
                  size={18}
                  className="transition-transform duration-300 group-hover:translate-x-1"
                />
              </button>
            </div>
          </motion.div>

          {/* Right Visual */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96, x: 20 }}
            whileInView={{ opacity: 1, scale: 1, x: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            viewport={{ once: true }}
            className="order-1 md:order-2 relative flex items-center justify-center"
          >
            {/* Glow */}
            <div className="absolute h-[320px] w-[320px] rounded-full bg-[#FF0000]/10 blur-3xl md:h-[440px] md:w-[440px]" />

            {/* Image Card */}
            <motion.div
              animate={{ y: [0, -8, 0] }}
              transition={{
                duration: 5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              className="relative w-full max-w-[580px]"
            >
              <div className="absolute inset-0 rounded-[2rem] bg-gradient-to-br from-white/70 to-white/30 shadow-[0_30px_80px_rgba(0,0,0,0.12)] backdrop-blur-2xl" />
              <div className="relative rounded-[2rem] border border-white/60 bg-white/70 p-4 shadow-[0_30px_80px_rgba(0,0,0,0.12)] backdrop-blur-2xl">
                <img
                  src="/Boxing/Categories/15.webp"
                  alt="IES Gear Stitching Hall"
                  className="w-full rounded-2xl object-cover"
                />
              </div>

              {/* Floating Info Card */}
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.5 }}
                viewport={{ once: true }}
                className="absolute -bottom-4 left-4 rounded-2xl border border-black/10 bg-white/90 px-4 py-3 shadow-xl backdrop-blur-xl md:-bottom-5 md:left-6"
              >
                <p className="text-xs font-medium text-gray-500">Trusted By</p>
                <p className="text-sm font-bold text-black md:text-base">
                  Athletes Worldwide
                </p>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HomeLearnMore;